import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Login from './components/Login';
import Register from './components/Register';
import VerifyDevice from './components/VerifyDevice';
import ResetPasswordPage from './components/Auth/ResetPasswordPage';
import Dashboard from './components/Dashboard';
import CustomerListPage from './components/Customers/CustomerListPage';
import CustomerDetailListPage from './components/Customers/CustomerDetailListPage';
import ProductListPage from './components/Products/ProductListPage';
import AdminDashboard from './components/Admin/AdminDashboard';
import UserManagement from './components/Admin/UserManagement';
import AuthLogs from './components/Admin/AuthLogs';
import BackupManagement from './components/Admin/BackupManagement';
import WebhookManagement from './components/Admin/WebhookManagement';
import OrderListPage from './components/Orders/OrderListPage';
import OrderForm from './components/Orders/OrderForm';
import PlanningPage from './components/Planning/PlanningPage';
import RunsheetPage from './components/Runsheet/RunsheetPage';
import DailyRunsheet from './components/Runsheet/DailyRunsheet';
import DeliveryRunsListPage from './components/Deliveries/DeliveryRunsListPage';
import VehicleAvailabilityPage from './components/Deliveries/VehicleAvailabilityPage';
import NavigationDrawer from './components/NavigationDrawer';
import MobileMenuToggle from './components/MobileMenuToggle';
import { authAPI } from './services/api';
import './App.css';

function AppContent({ user, handleLoginSuccess, handleLogout }) {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(true);
  
  // Determine if we should center the content (for auth screens)
  const shouldCenter = !user && (location.pathname === '/login' || location.pathname === '/register' || location.pathname.startsWith('/verify-device') || location.pathname.startsWith('/reset-password'));
  const showDrawer = user && !location.pathname.startsWith('/verify-device');

  return (
    <div className={`App ${shouldCenter ? 'centered' : ''} ${showDrawer ? 'app-with-drawer' : ''}`}>
      {showDrawer && (
        <>
          <NavigationDrawer
            user={user}
            onLogout={handleLogout}
            isOpen={drawerOpen}
            onToggle={() => setDrawerOpen(!drawerOpen)}
          />
          <MobileMenuToggle onToggle={() => setDrawerOpen(!drawerOpen)} />
        </>
      )}
      <div className={`${showDrawer ? `app-main-content ${drawerOpen ? 'drawer-open' : 'drawer-closed'}` : ''}`}>
        <Routes>
        <Route
          path="/login"
          element={
            user ? <Navigate to="/dashboard" /> : <Login onLoginSuccess={handleLoginSuccess} />
          }
        />
        <Route
          path="/register"
          element={
            user ? <Navigate to="/dashboard" /> : <Register onRegisterSuccess={() => window.location.href = '/login'} />
          }
        />
        <Route
          path="/verify-device/:token"
          element={<VerifyDevice />}
        />
        <Route
          path="/reset-password"
          element={<ResetPasswordPage />}
        />
          <Route
            path="/dashboard"
            element={
              user ? <Dashboard user={user} /> : <Navigate to="/login" />
            }
          />
          <Route
            path="/customers"
            element={
              user ? <CustomerListPage user={user} /> : <Navigate to="/login" />
            }
          />
          <Route
            path="/customers/:action"
            element={
              user ? <CustomerListPage user={user} /> : <Navigate to="/login" />
            }
          />
          <Route
            path="/customers/:action/:id"
            element={
              user ? <CustomerListPage user={user} /> : <Navigate to="/login" />
            }
          />
          <Route
            path="/customer-fields"
            element={
              user ? <CustomerDetailListPage user={user} /> : <Navigate to="/login" />
            }
          />
          <Route
            path="/orders"
            element={
              user ? <OrderListPage user={user} /> : <Navigate to="/login" />
            }
          />
          <Route
            path="/orders/:action"
            element={
              user ? <OrderListPage user={user} /> : <Navigate to="/login" />
            }
          />
          <Route
            path="/orders/:action/:id"
            element={
              user ? <OrderForm user={user} /> : <Navigate to="/login" />
            }
          />
          <Route
            path="/deliveries"
            element={
              user ? <PlanningPage user={user} /> : <Navigate to="/login" />
            }
          />
          <Route
            path="/deliveries/planning"
            element={
              user ? <PlanningPage user={user} /> : <Navigate to="/login" />
            }
          />
          <Route
            path="/deliveries/runs"
            element={
              user ? <DeliveryRunsListPage user={user} /> : <Navigate to="/login" />
            }
          />
          <Route
            path="/deliveries/runsheet"
            element={
              user ? <DailyRunsheet user={user} /> : <Navigate to="/login" />
            }
          />
          <Route
            path="/deliveries/runsheet/:runId"
            element={
              user ? <RunsheetPage user={user} /> : <Navigate to="/login" />
            }
          />
          <Route
            path="/deliveries/vehicles"
            element={
              user ? <VehicleAvailabilityPage user={user} /> : <Navigate to="/login" />
            }
          />
          <Route
            path="/admin"
            element={
              user && user.is_admin ? <AdminDashboard /> : <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/admin/users"
            element={
              user && user.is_admin ? <UserManagement /> : <Navigate to="/dashboard" />
            }
          />

          <Route
            path="/products"
            element={
              user && user.is_admin ? <ProductListPage user={user} /> : <Navigate to="/login" />
            }
          />
          <Route
            path="/products/:action"
            element={
              user && user.is_admin ? <ProductListPage user={user} /> : <Navigate to="/login" />
            }
          />
          <Route
            path="/products/:action/:id"
            element={
              user && user.is_admin ? <ProductListPage user={user} /> : <Navigate to="/login" />
            }
          />
          <Route
            path="/admin/logs"
            element={
              user && user.is_admin ? <AuthLogs /> : <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/admin/webhooks"
            element={
              user && user.is_admin ? <WebhookManagement /> : <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/admin/backups"
            element={
              user && user.is_admin ? <BackupManagement /> : <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/"
            element={<Navigate to={user ? "/dashboard" : "/login"} />}
          />
        </Routes>
      </div>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        try {
          const response = await authAPI.getMe();
          setUser(response.user);
        } catch (err) {
          // Token is invalid, clear it
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const handleLoginSuccess = (userData) => {
    setUser(userData);
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner-large"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <Router>
      <AppContent
        user={user}
        handleLoginSuccess={handleLoginSuccess}
        handleLogout={handleLogout}
      />
    </Router>
  );
}

export default App;
